import { inject } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivateFn,
    CanMatchFn,
    Route,
    Router,
    RouterStateSnapshot,
} from '@angular/router';
import { PermissionService } from '../../services/permission/permission.service';
import { AuthService } from '../../services/auth/auth.service';
import { AppointmentApiService } from '../../api/appointment-api.service';
import { LoggerService } from '../../services/logger/logger.service';
import {
    ROLES,
    APPOINTMENT_TYPES,
} from '../../constants/appointment.constants';

/**
 * Verifie si l'utilisateur possede l'un des roles requis.
 *
 * @param requiredRoles - Liste des roles autorises
 * @returns true si l'utilisateur a l'un des roles, false sinon
 */
async function checkUserRole(requiredRoles: string[]): Promise<boolean> {
    const authService = inject(AuthService);
    const permissions = inject(PermissionService);
    const router = inject(Router);

    await authService.rolesReady;

    if (!permissions.hasAnyRole(requiredRoles)) {
        router.navigate(['/']);
        return false;
    }

    return true;
}

/**
 * Guard de route basee sur les roles.
 * Utilise les roles definis dans route.data['roles'].
 */
export const roleGuard: CanActivateFn = (route) => {
    const requiredRoles = route.data?.['roles'] as string[];
    return checkUserRole(requiredRoles);
};

/**
 * Guard de chargement de module basee sur les roles.
 * Utilise les roles definis dans route.data['roles'].
 */
export const roleGuardLoad: CanMatchFn = (route: Route) => {
    const requiredRoles = route.data?.['roles'] as string[];
    return checkUserRole(requiredRoles);
};

/**
 * Guard specifique pour les rendez-vous.
 * Verifie les permissions d'acces selon le type de rendez-vous
 * et les roles de l'utilisateur.
 *
 * Regles d'acces:
 * - Comite: acces a tous les types
 * - Membre: acces a tous les types
 * - Chef char: acces a "Char" et "Char et costume"
 * - Chef costume: acces a "Costume" et "Char et costume"
 */
export const appointmentRoleGuard: CanActivateFn = async (
    route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot,
) => {
    const permissions = inject(PermissionService);
    const router = inject(Router);
    const appointmentApi = inject(AppointmentApiService);
    const logger = inject(LoggerService);

    // Validation du parametre de route
    const appointmentParam = route.paramMap.get('appointment');
    if (!appointmentParam) {
        return router.parseUrl('/');
    }

    if (!/^\d+$/.test(appointmentParam)) {
        return router.parseUrl('/');
    }
    const appointmentId = parseInt(appointmentParam, 10);
    if (appointmentId <= 0) {
        return router.parseUrl('/');
    }

    try {
        // Recuperation du rendez-vous
        const appointment = await appointmentApi.getById(appointmentId);

        if (!appointment) {
            return router.parseUrl('/');
        }

        const appointmentName: string = appointment.name as string;

        // 1) Comite: acces a tout
        if (permissions.hasRole(ROLES.COMITE)) {
            return true;
        }

        // 2) Membre: acces a tous les rendez-vous
        if (permissions.hasRole(ROLES.MEMBRE)) {
            return true;
        }

        // 3) Verification par type de rendez-vous
        const isChar = appointmentName === APPOINTMENT_TYPES.CHAR;
        const isCostume = appointmentName === APPOINTMENT_TYPES.COSTUME;
        const isCharAndCostume =
            appointmentName === APPOINTMENT_TYPES.CHAR_ET_COSTUME;

        // Chef char -> Char + Char et costume
        if (
            permissions.hasRole(ROLES.CHEF_CHAR) &&
            (isChar || isCharAndCostume)
        ) {
            return true;
        }

        // Chef costume -> Costume + Char et costume
        if (
            permissions.hasRole(ROLES.CHEF_COSTUME) &&
            (isCostume || isCharAndCostume)
        ) {
            return true;
        }

        // Chef premier mai → Répétition premier mai + Premier mai
        const isRepetitionPremierMai =
            appointmentName === APPOINTMENT_TYPES.REPETITION_PREMIER_MAI;
        const isPremierMai = appointmentName === APPOINTMENT_TYPES.PREMIER_MAI;

        if (
            permissions.hasRole(ROLES.CHEF_PREMIER_MAI) &&
            (isRepetitionPremierMai || isPremierMai)
        ) {
            return true;
        }

        // 4) Refus par defaut
        return router.parseUrl('/');
    } catch (error) {
        logger.error('Erreur lors de la verification des roles RDV:', error);
        return router.parseUrl('/');
    }
};
